/**
 * What makes a quiz "complete".
 *
 * `users.quiz_completed_at` is stamped by the profile save the moment every
 * one of these answers is present, and nothing else in the product reads a
 * profile as finished until it is: `ProtectedRoute` sends a signed-in user
 * without it back to `/onboarding` on every page.
 *
 * That makes this list the contract between three places that used to each
 * carry their own copy: the server's stamp, the client's signup event, and the
 * flow's resume, which has to open on whichever of these is still missing.
 * One copy, so they cannot disagree about who is done.
 */
export const REQUIRED_QUIZ_FIELDS = [
  "location",
  "dinnerVibe",
  "conversationTopics",
  "genderIdentity",
  "sexualOrientation",
  "birthday",
] as const;

export type RequiredQuizField = (typeof REQUIRED_QUIZ_FIELDS)[number];

/**
 * Whether a single answer counts. An empty array is a multi-select nobody
 * picked from, and an empty string is a cleared field — neither is an answer.
 */
export function isQuizFieldAnswered(value: unknown): boolean {
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim().length > 0;
  return value !== null && value !== undefined && value !== false;
}

/** The required answers this profile does not have yet, in canonical order. */
export function missingQuizFields(
  answers: Record<string, unknown> | null | undefined,
): RequiredQuizField[] {
  return REQUIRED_QUIZ_FIELDS.filter(
    (key) => !isQuizFieldAnswered(answers?.[key]),
  );
}

/** True once every required answer is present. */
export function isQuizComplete(
  answers: Record<string, unknown> | null | undefined,
): boolean {
  return missingQuizFields(answers).length === 0;
}
